import { OS } from '../tools'
import { wxSignature } from '../services/index'

const JSSDK_URL = '//res.wx.qq.com/open/js/jweixin-1.6.0.js'
const DEFAULT_SHARE_IMG = '/timesloanh5/brand/assets/imgs/logo.png'
const BRAND_PATH_PREFIX = '/timesloanh5/brand'

let wxConfigReady = false
let lastShareOptions = null

function logShare() {
    console.log.apply(console, ['[wx-share]'].concat([].slice.call(arguments)))
}

function isWxDebug() {
    return /[?&]wxdebug=1(?:&|$)/.test(location.href)
}

function loadWxSdk() {
    if (typeof wx !== 'undefined') {
        return Promise.resolve()
    }
    return new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.src = JSSDK_URL
        script.onload = resolve
        script.onerror = reject
        document.head.appendChild(script)
    })
}

function normalizeBrandEntryUrl() {
    const { pathname, search, hash } = window.location
    const isBrandRoot =
        pathname === BRAND_PATH_PREFIX ||
        pathname === BRAND_PATH_PREFIX + '/'

    if (!isBrandRoot) {
        return
    }

    const nextPath = BRAND_PATH_PREFIX + '/index.html'
    history.replaceState(null, '', nextPath + search + hash)
    window.__wxSignPageUrl = location.href.split('#')[0]
}

function getWxSignPageUrl() {
    normalizeBrandEntryUrl()
    return window.__wxSignPageUrl || location.href.split('#')[0]
}

/**
 * 分享链接：与当前页同域，且带 index.html（官方要求 link 域名须在 JS 安全域名内）
 */
export function getWechatShareLink() {
    normalizeBrandEntryUrl()
    let href = location.href
    if (/\/timesloanh5\/brand#\//.test(href)) {
        href = href.replace('/timesloanh5/brand#/', '/timesloanh5/brand/index.html#/')
    }
    if (/\/timesloanh5\/brand\/#\//.test(href)) {
        href = href.replace('/timesloanh5/brand/#/', '/timesloanh5/brand/index.html#/')
    }
    return href
}

function setMetaTag(property, content) {
    if (!content) return
    let el = document.querySelector(`meta[property="${property}"]`)
    if (!el) {
        el = document.createElement('meta')
        el.setAttribute('property', property)
        document.head.appendChild(el)
    }
    el.setAttribute('content', content)
}

function updatePageMeta({ title, desc, imgUrl }) {
    if (title) {
        document.title = title
        setMetaTag('og:title', title)
    }
    if (desc) {
        setMetaTag('og:description', desc)
    }
    if (imgUrl) {
        setMetaTag('og:image', imgUrl)
    }
}

/**
 * 官方文档：须在 wx.ready 内、用户点击分享前调用 updateAppMessageShareData
 * https://developers.weixin.qq.com/doc/offiaccount/OA_Web_Apps/JS-SDK.html
 */
function logWxApiFail(apiName, err) {
    const msg = (err && err.errMsg) || ''
    logShare(apiName + ' fail', err)
    if (msg.indexOf('offline verifying') > -1) {
        logShare(
            'offline verifying：后台已显示分享接口有权限时，多为签名服务 appId/jsapi_ticket 与当前公众号不一致，' +
                '或访问域名不在 JS 安全域名列表、ticket 缓存过期。请后端核对 jsSignature 使用的 AppID 与 test 环境域名。'
        )
    }
}

function setupShareInReady() {
    if (!wxConfigReady || !lastShareOptions) return

    const { title, desc, link, imgUrl } = lastShareOptions
    logShare('wx.ready 设置分享', { title, desc, link, imgUrl })

    // 仅调试时检测权限，避免开发者工具 nopermissionJsApi / verifyJsApiList 干扰
    if (isWxDebug() && wx.checkJsApi) {
        wx.checkJsApi({
            jsApiList: ['updateAppMessageShareData', 'updateTimelineShareData'],
            success(res) {
                logShare('checkJsApi', res.checkResult)
            },
            fail(err) {
                logShare('checkJsApi fail', err)
            }
        })
    }

    // 不调用 showMenuItems：未加入 jsApiList 且权限审核中会报 offline verifying
    wx.updateAppMessageShareData({
        title,
        desc,
        link,
        imgUrl,
        success() {
            logShare('updateAppMessageShareData ok')
        },
        fail(err) {
            logWxApiFail('updateAppMessageShareData', err)
        }
    })

    wx.updateTimelineShareData({
        title,
        link,
        imgUrl,
        success() {
            logShare('updateTimelineShareData ok')
        },
        fail(err) {
            logWxApiFail('updateTimelineShareData', err)
        }
    })

    // 后台已开通「自定义分享消息/朋友圈」时，旧版接口作真机兜底
    if (wx.onMenuShareAppMessage) {
        wx.onMenuShareAppMessage({ title, desc, link, imgUrl, type: 'link' })
    }
    if (wx.onMenuShareTimeline) {
        wx.onMenuShareTimeline({ title, link, imgUrl })
    }
}

function configWxSdk(pageUrl) {
    logShare('签名 pageUrl', pageUrl)

    return wxSignature(pageUrl).then((data) => {
        const timestamp = Number(data.timestamp || data.timeStamp)

        logShare('签名返回 appId', data.appId, '当前访问 host', location.host)

        return new Promise((resolve, reject) => {
            wx.config({
                debug: isWxDebug(),
                appId: data.appId,
                timestamp,
                nonceStr: data.nonceStr,
                signature: data.signature,
                jsApiList: [
                    'updateAppMessageShareData',
                    'updateTimelineShareData',
                    'onMenuShareAppMessage',
                    'onMenuShareTimeline'
                ]
            })

            wx.ready(() => {
                wxConfigReady = true
                logShare('wx.config ok')
                setupShareInReady()
                resolve()
            })

            wx.error((err) => {
                logShare('wx.config fail', err)
                reject(err)
            })
        })
    })
}

function bindShareOnPageShow() {
    if (window.__wxSharePageShowBound) return
    window.__wxSharePageShowBound = true
    window.addEventListener('pageshow', () => {
        logShare('pageshow 重新设置分享')
        setupShareInReady()
    })
}

export function initWechatShare(options = {}) {
    if (!OS.isWechat) {
        return Promise.resolve()
    }

    const pageUrl = getWxSignPageUrl()
    const {
        title = '万达商户贷',
        desc = '万达广场商户专属，最高额度50万，当天放款，循环额度>>',
        link = getWechatShareLink(),
        imgUrl = location.origin + DEFAULT_SHARE_IMG
    } = options

    lastShareOptions = {
        title,
        desc,
        link: link || getWechatShareLink(),
        imgUrl
    }

    updatePageMeta(lastShareOptions)
    bindShareOnPageShow()

    logShare('开始初始化', lastShareOptions, 'host', location.host)

    return loadWxSdk()
        .then(() => configWxSdk(pageUrl))
        .catch((err) => {
            logShare('初始化失败', err)
        })
}



// WEBPACK FOOTER //
// ./src/utils/libs/wechat-share.js